import {
  Box,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import AutoComplete from '../../components/AutoComplete';
import SearchTextField from '../../components/SearchTextField';
import TableSkeleton from '../../components/TableSkeleton';
import { useStoryPriceSettingScheduleMutation, useStoryPriceSettingSchedules } from '../../hooks/queries/useStoryPriceSettingSchedule';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { useQueryStringState } from '../../hooks/useQueryStringState';
import useTablePagination from '../../hooks/useTablePagination';
import settings from '../../services/settings';
import { IStoryPriceSettingSchedule, ISuggestionSource } from '../../services/types';

interface IProps {}

const useStyles = makeStyles((theme: Theme) => ({
  search: {
    width: 360,
  },

  formLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
  },

  startedAtTableCell: {
    width: 180,
  },

  tableRow: {
    height: 52,
  },
}));

enum PriceFilterValue {
  All = 'all',
  NoSale = '3',
  Coin2 = '2',
  Coin1 = '1',
}

export enum StatusFilterValue {
  All = 'all',
  Applied = 'applied',
  Scheduled = 'scheduled',
}

const SaleStoryList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination();

  const [storyId, setStoryId] = useQueryStringState<string>('storyId');
  const [priceFilterValue, setPriceFilterValue] = useQueryStringState<PriceFilterValue>('price', PriceFilterValue.All);
  const [statusFilterValue, setStatusFilterValue] = useQueryStringState<StatusFilterValue>('status', StatusFilterValue.All);

  const confirm = useConfirmDialog();

  const { data, isLoading } = useStoryPriceSettingSchedules(
    page,
    rowsPerPage,
    storyId ? Number(storyId) : undefined,
    priceFilterValue !== PriceFilterValue.All ? Number(priceFilterValue) : undefined,
    priceFilterValue === PriceFilterValue.All && statusFilterValue !== StatusFilterValue.All ? statusFilterValue : undefined,
  );

  const { remove } = useStoryPriceSettingScheduleMutation();

  const schedules = data?.items;
  const totalCount = data?.totalCount;

  const handlePriceFilterChange = (_: any, value: string) => {
    if (value !== PriceFilterValue.All) {
      setStatusFilterValue(StatusFilterValue.All);
    }

    setPriceFilterValue(value as PriceFilterValue);
  };
  const handleStatusFilterChange = (_: any, value: string) => {
    setStatusFilterValue(value as StatusFilterValue);
  };

  const handleSearchSubmit = (query: string) => {
    if (!/\d+/.test(query)) {
      return;
    }

    setStoryId(query);
    setPage(1);
  };

  const handleSearchCancel = () => {
    setStoryId(undefined);
    setPage(1);
  };

  const handleDeleteClick = (schedule: IStoryPriceSettingSchedule) => {
    confirm.open({
      title: 'Are you sure you want to delete this schedule?',
      onProceed: () => remove.mutate(schedule),
    });
  };

  const getSaleText = (episodePrice: number): string => {
    switch (episodePrice) {
      case 0:
        return 'Free';
      case 2:
        return '33% Off';
      case 1:
        return '66% Off';
      default:
        return '-';
    }
  };

  const isStatusFilterDisabled = priceFilterValue !== PriceFilterValue.All;

  return (
    <Box>
      <Box marginBottom={1}>
        <FormLabel className={classes.formLabel}>Filter by Sale Type</FormLabel>
        <RadioGroup row value={String(priceFilterValue)} onChange={handlePriceFilterChange}>
          <FormControlLabel value={PriceFilterValue.All} control={<Radio color="primary" />} label="All Prices" />
          <FormControlLabel value={PriceFilterValue.NoSale} control={<Radio color="primary" />} label="3 Coins" />
          <FormControlLabel value={PriceFilterValue.Coin2} control={<Radio color="primary" />} label="2 Coins (33% OFF)" />
          <FormControlLabel value={PriceFilterValue.Coin1} control={<Radio color="primary" />} label="1 Coin (66% OFF)" />
        </RadioGroup>
      </Box>

      <Box display="flex" marginBottom={2}>
        <Box flex="1">
          <FormLabel className={classes.formLabel}>Filter by Status</FormLabel>
          <RadioGroup row value={statusFilterValue} onChange={handleStatusFilterChange}>
            <FormControlLabel
              value={StatusFilterValue.All}
              control={<Radio color="primary" />}
              label="All Status"
              disabled={isStatusFilterDisabled}
            />
            <FormControlLabel
              value={StatusFilterValue.Applied}
              control={<Radio color="primary" />}
              label="Applied"
              disabled={isStatusFilterDisabled}
            />
            <FormControlLabel
              value={StatusFilterValue.Scheduled}
              control={<Radio color="primary" />}
              label="Scheduled"
              disabled={isStatusFilterDisabled}
            />
          </RadioGroup>
        </Box>
        <Box display="flex" flex="1" justifyContent="flex-end" alignItems="flex-end">
          <AutoComplete<any>
            type="story"
            render={(props: TextFieldProps) => {
              const { defaultValue, ...restProps } = props;
              return (
                <SearchTextField
                  {...restProps}
                  defaultValue={storyId || (defaultValue as string)}
                  placeholder="Enter Story Title or Story Id"
                  className={classes.search}
                  onValueSubmit={handleSearchSubmit}
                  isSearching={isLoading}
                  onCancel={handleSearchCancel}
                />
              );
            }}
            onSelect={(source: ISuggestionSource) => {
              setStoryId(source.id);
            }}
          />
        </Box>
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableCell align="center">Episode Price</TableCell>
            <TableCell align="center">Sale</TableCell>
            <TableCell align="center">Story Id</TableCell>
            <TableCell>Story Title</TableCell>
            <TableCell className={classes.startedAtTableCell}>Started at</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Delete</TableCell>
          </TableHead>
          <TableBody>
            {isLoading && <TableSkeleton rows={rowsPerPage} columns={8} />}
            {!isLoading &&
              schedules &&
              schedules.map(schedule => {
                const { id, storyId, story, price, startsAt, appliedAt } = schedule;
                const { title } = story;
                const indelible = Boolean(appliedAt);

                return (
                  <TableRow key={id} hover className={classes.tableRow}>
                    <TableCell align="center">{price}</TableCell>
                    <TableCell align="center">{getSaleText(price)}</TableCell>
                    <TableCell align="center">{storyId}</TableCell>
                    <TableCell>
                      <Link href={`${settings.getCrewURI()}/stories/${storyId}`} target="_blank">
                        {title}
                      </Link>
                    </TableCell>
                    <TableCell>{moment(startsAt).format('LLL')}</TableCell>
                    <TableCell align="center">{appliedAt ? 'Applied' : 'Scheduled'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={'The applied schedule cannot be deleted.'} placement="bottom" arrow open={!indelible ? false : undefined}>
                        <IconButton
                          style={{
                            opacity: indelible ? 0.5 : 1,
                          }}
                          color={indelible ? 'default' : 'primary'}
                          disableFocusRipple={indelible}
                          disableTouchRipple={indelible}
                          disableRipple={indelible}
                          onClick={e => {
                            if (indelible) {
                              return;
                            }

                            e.preventDefault();
                            e.stopPropagation();

                            handleDeleteClick(schedule);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {!isLoading && isEmpty(schedules) && (
          <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body1">{`There is no schedule ${storyId ? 'for this story' : 'with this condition'}. Please try again.`}</Typography>
          </Box>
        )}
        {totalCount !== undefined && (
          <TablePagination
            count={totalCount || -1} // an unknown number of items, provide -1. (https://material-ui.com/api/table-pagination/)
            rowsPerPage={rowsPerPage}
            page={page - 1} // TablePagination has the zero-based index of the current page.
            onChangePage={(_: unknown, p: number) => setPage(p + 1)}
            onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
              const rpp = parseInt(e.target.value, 10);
              setRowsPerPage(rpp);
              setPage(1);
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default SaleStoryList;
